import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardList, Route, Rocket, Youtube, Target, Award, Sparkles, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroRoadmap } from "@/components/HeroRoadmap";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const PROPS = [
  { icon: Target, title: "Built around you", line: "A 2-minute quiz turns into your personal AI curriculum." },
  { icon: Youtube, title: "Only the best videos", line: "We judge teaching quality, not views — the top of YouTube, in order." },
  { icon: Sparkles, title: "Always fresh", line: "New videos flow into your path daily. It never goes stale." },
];

const STEPS = [
  { icon: ClipboardList, num: "01", title: "Take the quiz", line: "Your level, goal & language." },
  { icon: Route, num: "02", title: "Get your roadmap", line: "The best free lesson for every step." },
  { icon: Rocket, num: "03", title: "Learn & level up", line: "Summaries, quizzes, streaks." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const start = () => {
    if (user) return navigate(user.has_path ? "/dashboard" : "/quiz");
    navigate("/quiz");
  };

  const STAT_ITEMS = [
    { value: stats ? `${stats.learners}+` : "—", label: "Learners" },
    { value: stats ? stats.videos.toLocaleString() : "—", label: "Curated videos" },
    { value: stats ? stats.tools : "—", label: "AI tool courses" },
    { value: "3", label: "Languages" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO — the whole pitch in 2 lines */}
      <section className="relative overflow-hidden mesh-hero grid-paper pt-32 pb-16 md:pt-40 md:pb-24">
        <span className="orb h-80 w-80 bg-brand-cyber -top-20 -left-20" />
        <span className="orb h-72 w-72 bg-violet-400 top-1/3 -right-16" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-headline"
              className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            >
              Learn AI. <span className="gradient-text">Or get left behind.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-subtext"
              className="mt-5 text-base md:text-lg text-muted-foreground max-w-md"
            >
              Your personal AI course — built from the best free YouTube videos, in your language.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex items-center gap-5 flex-wrap"
            >
              <Button size="lg" data-testid="hero-cta" onClick={start} className="rounded-full px-8 h-12 text-base">
                {user ? "Continue learning" : "Build my AI path — free"} <ArrowRight className="h-5 w-5" />
              </Button>
              <span className="text-xs text-muted-foreground">2-minute quiz · no card needed</span>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.36 }}
              data-testid="hero-refund-badge"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400"
            >
              <ShieldCheck className="h-4 w-4" /> 7-day money-back guarantee — one click, no questions asked
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md w-full mx-auto lg:ml-auto rounded-2xl overflow-hidden"
            data-testid="hero-visual"
          >
            <HeroRoadmap />
          </motion.div>
        </div>
      </section>

      {/* 3 SHARP REASONS — one line each */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-5">
          {PROPS.map((p, i) => (
            <motion.div key={p.title} data-testid={`value-prop-${i + 1}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: i * 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 shadow-brutal-sm">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary">
                <p.icon className="h-5 w-5" />
              </span>
              <h2 className="mt-4 font-heading font-bold text-lg">{p.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{p.line}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — 3 steps, one line each */}
      <section id="how-it-works" data-testid="how-it-works" className="py-14 md:py-20 border-y border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }} className="font-heading font-extrabold text-2xl md:text-4xl text-center">
            Zero to pro in <span className="gradient-text">3 steps</span>
          </motion.h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.num} data-testid={`how-step-${i + 1}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="rounded-2xl bg-card border border-border p-7 text-center shadow-brutal-sm">
                <span className="font-mono text-[10px] font-bold text-muted-foreground tracking-[0.3em]">{s.num}</span>
                <span className="mx-auto mt-4 grid place-items-center h-14 w-14 rounded-2xl bg-primary/10 text-primary">
                  <s.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-4 font-heading font-bold text-lg">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.line}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS + FINAL CTA */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <span className="orb h-72 w-72 bg-brand-cyber bottom-0 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }} data-testid="stats-bar"
            className="rounded-3xl border border-border bg-card shadow-brutal px-6 py-8 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {STAT_ITEMS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-heading font-extrabold text-3xl md:text-4xl gradient-text">{s.value}</p>
                <p className="mt-1 text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }} className="mt-14 text-center">
            <Award className="h-8 w-8 text-primary mx-auto" />
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-5xl tracking-tight">
              Your AI journey starts <span className="gradient-text">today.</span>
            </h2>
            <Button size="lg" data-testid="final-cta" onClick={start} className="mt-7 rounded-full px-9 h-12 text-base">
              {user ? "Continue learning" : "Build my AI path — free"} <ArrowRight className="h-5 w-5" />
            </Button>
            <p data-testid="final-refund-badge" className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 w-full">
              <ShieldCheck className="h-4 w-4" /> Risk-free: 7-day no-questions-asked money-back guarantee
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
